import * as React from 'react'

import {
  TQOrderForCartVariables,
  TQWaiterCartVariables,
  useQOrderForCart,
  useQWaiterCart,
} from '~graphql/generated/clientGraphql'

import { useCustomerOperationContext } from '../../CustomerAccounts'

type TVariables = TQOrderForCartVariables | TQWaiterCartVariables

type TQuery = typeof useQOrderForCart | typeof useQWaiterCart
type TOptions<TUseQueryHook extends TQuery> = {
  /**
   * id of the order to load the cart content for
   */
  orderId: NullableID
  useQCart: TUseQueryHook
  /**
   * If true, the customer context will be used to load the cart content
   *
   * It is needed in the customer profile to show order detail in history
   * if there is the `userToken` in the URL
   */
  withCustomerContext?: boolean
}

/**
 * (Re)load order for cart content whenever any of the provided options changes.
 */
export const useLoadCartContent = <TUseQueryHook extends TQuery>({
  orderId,
  useQCart,
  withCustomerContext = false,
}: TOptions<TUseQueryHook>) => {
  const customerContext = useCustomerOperationContext()

  const variables = React.useMemo<TVariables | undefined>(() => {
    return orderId ? { orderId } : undefined
  }, [orderId])

  const { data, loading, refetch } = useQCart({
    variables,
    // if the orderId is still loading (eg. creating an order) it could be null
    // we want to be able to render the cart in the meantime (in some loading)
    skip: !variables,
    fetchPolicy: `network-only`,
    context: withCustomerContext ? customerContext : undefined,
  })

  // TODO should this be memoized somehow to prevent infinite loops or is it by default?
  // or maybe transform it into a callable function (on demand) instead of reacting hook
  // in theory, the Q above should rerun only if one of its variables or context changes
  // but it needs testing (remove these comments if it works as intended)
  const result = React.useMemo(() => {
    if (loading) {
      return {
        loading,
        order: data?.order ?? null,
      }
    }

    if (!data) {
      return {
        loading,
        order: null,
      }
    }

    return {
      order: data.order,
    }
  }, [data, loading])

  return { ...result, refetch }
}
